import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useState } from "react"
import {
  getKycDocumentUrl,
  getKycReviewDetail,
  getMyReviewerProductivity,
  listKycQueue,
  requestMoreKycInfo,
  submitKycReview,
} from "../api/endpoints/kyc"
import { Button } from "../components/ui/button"
import { DataTable, TableBody, TableHead, TableRow, Td, Th } from "../components/ui/data_table"
import { Drawer } from "../components/ui/drawer"
import { EmptyState } from "../components/ui/empty_state"
import { FormField, Input, Textarea } from "../components/ui/input"
import { Modal } from "../components/ui/modal"
import { StatCard } from "../components/ui/stat_card"
import { StatusPill } from "../components/ui/status_pill"
import { useAuth } from "../stores/auth"
import { VerificationMonitoringPage } from "./verification_monitor"

const DEFAULT_CHECKLIST = [
  "Identity document is clear and legible",
  "Photo matches selfie",
  "Name on ID matches application name",
  "Date of birth matches ID",
  "ID is not expired",
  "Physical address is verifiable",
  "Next of kin information is complete",
]

const REQUESTABLE_ITEMS = [
  { itemKind: "DOCUMENT", itemKey: "NATIONAL_ID_FRONT", label: "National ID front" },
  { itemKind: "DOCUMENT", itemKey: "NATIONAL_ID_BACK", label: "National ID back" },
  { itemKind: "DOCUMENT", itemKey: "SELFIE", label: "Selfie" },
  { itemKind: "DOCUMENT", itemKey: "PROOF_OF_INCOME", label: "Proof of income" },
  { itemKind: "DOCUMENT", itemKey: "PROOF_OF_ADDRESS", label: "Proof of address" },
  { itemKind: "PROFILE_FIELD", itemKey: "FULL_NAME", label: "Full name" },
  { itemKind: "PROFILE_FIELD", itemKey: "DATE_OF_BIRTH", label: "Date of birth" },
  { itemKind: "PROFILE_FIELD", itemKey: "GENDER", label: "Gender" },
  { itemKind: "PROFILE_FIELD", itemKey: "ALT_PHONE", label: "Alternative phone" },
  { itemKind: "PROFILE_FIELD", itemKey: "PHYSICAL_ADDRESS", label: "Physical address" },
  { itemKind: "PROFILE_FIELD", itemKey: "NATIONAL_ID", label: "National ID number" },
  { itemKind: "NEXT_OF_KIN_FIELD", itemKey: "FULL_NAME", label: "Next of kin name" },
  { itemKind: "NEXT_OF_KIN_FIELD", itemKey: "RELATIONSHIP", label: "Next of kin relationship" },
  { itemKind: "NEXT_OF_KIN_FIELD", itemKey: "PHONE_NUMBER", label: "Next of kin phone number" },
] as const

interface ChecklistItem {
  item: string
  passed: boolean
  note: string
}

interface RequestItemDraft {
  itemKind: (typeof REQUESTABLE_ITEMS)[number]["itemKind"]
  itemKey: (typeof REQUESTABLE_ITEMS)[number]["itemKey"]
  label: string
  selected: boolean
  instructions: string
}

type QueueStatusFilter = "OPEN" | "PENDING" | "ACTION_REQUIRED"

interface DocumentViewerState {
  clientId: string
  document: any
  documentType: string
}

function createDefaultChecklist() {
  return DEFAULT_CHECKLIST.map((item) => ({ item, passed: false, note: "" }))
}

function createRequestDrafts(): RequestItemDraft[] {
  return REQUESTABLE_ITEMS.map((item) => ({
    ...item,
    selected: false,
    instructions: "",
  }))
}

function kycTone(status?: string): "neutral" | "success" | "warning" | "danger" | "info" {
  if (status === "APPROVED") return "success"
  if (status === "REJECTED") return "danger"
  if (status === "ACTION_REQUIRED") return "info"
  if (status === "PENDING") return "warning"
  return "neutral"
}

function formatDateTime(value?: string | null) {
  if (!value) return "-"
  return new Date(value).toLocaleString()
}

function isImageDocument(document: any) {
  return document.mimeType?.startsWith("image/")
}

function queueLabel(statusFilter: QueueStatusFilter) {
  if (statusFilter === "OPEN") return "Open Cases"
  if (statusFilter === "ACTION_REQUIRED") return "Waiting On Client"
  return "Pending Reviews"
}

function queueEmptyState(statusFilter: QueueStatusFilter) {
  if (statusFilter === "ACTION_REQUIRED") {
    return {
      title: "No active follow-up requests",
      description: "No KYC cases are currently waiting on borrower updates.",
    }
  }

  if (statusFilter === "OPEN") {
    return {
      title: "No open KYC cases",
      description: "There are no pending reviews or borrower follow-ups right now.",
    }
  }

  return {
    title: "No pending reviews",
    description: "All pending KYC applications have been reviewed. Check back later.",
  }
}

function renderDocumentPreview(
  clientId: string,
  document: any,
  onOpen: (document: any) => void,
) {
  if (isImageDocument(document)) {
    return (
      <button type="button" className="block w-full text-left" onClick={() => onOpen(document)}>
        <img
          src={getKycDocumentUrl(clientId, document.documentId)}
          alt={document.fileName}
          className="w-full h-32 object-cover rounded border border-slate-100 transition-shadow hover:shadow-md"
          loading="lazy"
        />
      </button>
    )
  }

  return (
    <button
      type="button"
      onClick={() => onOpen(document)}
      className="flex w-full items-center justify-center rounded border border-slate-200 bg-slate-50 px-3 py-6 text-center text-xs font-semibold text-blue-600 transition-colors hover:bg-slate-100"
    >
      Open {document.fileName}
    </button>
  )
}

export function VerificationQueuePage() {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const canReviewKyc = Boolean(user?.permissions?.canReviewKyc)
  const [search, setSearch] = useState("")
  const [statusFilter, setStatusFilter] = useState<QueueStatusFilter>("OPEN")
  const [page, setPage] = useState(1)
  const [selectedClientId, setSelectedClientId] = useState<string | null>(null)
  const [documentViewer, setDocumentViewer] = useState<DocumentViewerState | null>(null)
  const [checklist, setChecklist] = useState<ChecklistItem[]>(createDefaultChecklist())
  const [rejectionReason, setRejectionReason] = useState("")
  const [requestMessage, setRequestMessage] = useState("")
  const [requestItems, setRequestItems] = useState<RequestItemDraft[]>(createRequestDrafts())
  const [confirmModal, setConfirmModal] = useState<
    "APPROVED" | "REJECTED" | "REQUEST_MORE_INFO" | null
  >(null)

  const queueQuery = useQuery({
    queryKey: ["kyc-queue", search, statusFilter, page],
    queryFn: () =>
      listKycQueue({
        ...(search ? { search } : {}),
        status: statusFilter,
        page: String(page),
        per_page: "20",
      }),
    enabled: canReviewKyc,
  })

  const productivityQuery = useQuery({
    queryKey: ["kyc-productivity", "me"],
    queryFn: getMyReviewerProductivity,
    enabled: canReviewKyc,
  })

  const detailQuery = useQuery({
    queryKey: ["kyc-review-detail", selectedClientId],
    queryFn: () => getKycReviewDetail(selectedClientId!),
    enabled: canReviewKyc && !!selectedClientId,
  })

  const reviewMutation = useMutation({
    mutationFn: (payload: { decision: string; rejectionReason?: string; checklist: ChecklistItem[] }) =>
      submitKycReview(selectedClientId!, {
        decision: payload.decision,
        rejection_reason: payload.rejectionReason,
        checklist: payload.checklist,
      }),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["kyc-queue"] }),
        queryClient.invalidateQueries({ queryKey: ["kyc-productivity"] }),
        queryClient.invalidateQueries({ queryKey: ["kyc-review-detail"] }),
      ])
      closeDrawer()
    },
  })

  const requestMutation = useMutation({
    mutationFn: (payload: { message: string; items: Array<Record<string, string>> }) =>
      requestMoreKycInfo(selectedClientId!, payload),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["kyc-queue"] }),
        queryClient.invalidateQueries({ queryKey: ["kyc-review-detail"] }),
      ])
      closeDrawer()
    },
  })

  if (!canReviewKyc) {
    return <VerificationMonitoringPage />
  }

  function resetDecisionForms() {
    setChecklist(createDefaultChecklist())
    setRejectionReason("")
    setRequestMessage("")
    setRequestItems(createRequestDrafts())
    setConfirmModal(null)
  }

  function closeDrawer() {
    setSelectedClientId(null)
    setDocumentViewer(null)
    resetDecisionForms()
  }

  function openDrawer(clientId: string) {
    resetDecisionForms()
    setSelectedClientId(clientId)
  }

  function openDocumentViewer(clientId: string, documentType: string, document: any) {
    setDocumentViewer({
      clientId,
      documentType,
      document,
    })
  }

  function updateRequestItem(
    itemKind: RequestItemDraft["itemKind"],
    itemKey: RequestItemDraft["itemKey"],
    updater: (current: RequestItemDraft) => RequestItemDraft,
  ) {
    setRequestItems((current) =>
      current.map((item) =>
        item.itemKind === itemKind && item.itemKey === itemKey ? updater(item) : item,
      ),
    )
  }

  function validateRequestDraft() {
    if (!requestMessage.trim()) {
      alert("Please provide a summary message for the borrower.")
      return false
    }

    const selectedItems = requestItems.filter((item) => item.selected)
    if (selectedItems.length === 0) {
      alert("Select at least one document or field to request.")
      return false
    }

    if (selectedItems.some((item) => !item.instructions.trim())) {
      alert("Every selected request item needs instructions for the borrower.")
      return false
    }

    return true
  }

  function handleConfirm() {
    if (!confirmModal) return

    if (confirmModal === "REQUEST_MORE_INFO") {
      requestMutation.mutate({
        message: requestMessage.trim(),
        items: requestItems
          .filter((item) => item.selected)
          .map((item) => ({
            item_kind: item.itemKind,
            item_key: item.itemKey,
            instructions: item.instructions.trim(),
          })),
      })
      return
    }

    reviewMutation.mutate({
      decision: confirmModal,
      rejectionReason: confirmModal === "REJECTED" ? rejectionReason : undefined,
      checklist,
    })
  }

  const items = queueQuery.data?.data ?? []
  const meta = queueQuery.data?.meta
  const prod = productivityQuery.data?.data
  const detail = detailQuery.data?.data
  const isWaitingOnClient = detail?.kycStatus === "ACTION_REQUIRED"
  const canTakeReviewAction = detail?.kycStatus === "PENDING"
  const selectedRequestItems = requestItems.filter((item) => item.selected)
  const emptyState = queueEmptyState(statusFilter)

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-800">Verification Queue</h2>
          <p className="text-sm text-slate-500 mt-0.5">
            Review pending KYC applications and monitor borrower follow-up cases that still need attention.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <select
            className="rounded-md border border-slate-200 bg-white px-3 py-2 text-sm"
            value={statusFilter}
            onChange={(event) => {
              setStatusFilter(event.target.value as QueueStatusFilter)
              setPage(1)
            }}
          >
            <option value="OPEN">All open cases</option>
            <option value="PENDING">Pending review</option>
            <option value="ACTION_REQUIRED">Waiting on client</option>
          </select>
          <Input
            placeholder="Search by name, phone, ID..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value)
              setPage(1)
            }}
            className="w-full sm:w-64"
          />
          <Button variant="secondary" size="sm" onClick={() => queueQuery.refetch()}>
            Refresh
          </Button>
        </div>
      </div>

      {prod && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <StatCard
            label={queueLabel(statusFilter)}
            value={meta?.total ?? 0}
            borderColor={
              statusFilter === "ACTION_REQUIRED"
                ? "border-blue-500"
                : statusFilter === "OPEN"
                  ? "border-slate-500"
                  : "border-amber-500"
            }
          />
          <StatCard
            label="Completed (Total)"
            value={prod.totalReviews ?? 0}
            borderColor="border-blue-500"
          />
          <StatCard
            label="Approved"
            value={prod.approvedCount ?? 0}
            helper={prod.totalReviews > 0 ? `${prod.approvalRate}% approval rate` : undefined}
            borderColor="border-emerald-500"
          />
          <StatCard
            label="Rejected"
            value={prod.rejectedCount ?? 0}
            helper={`${prod.reviewsThisWeek ?? 0} this week`}
            borderColor="border-red-500"
          />
        </div>
      )}

      <section className="bg-white rounded-lg shadow-sm border border-slate-200">
        {items.length === 0 ? (
          <div className="p-8">
            <EmptyState
              title={emptyState.title}
              description={emptyState.description}
            />
          </div>
        ) : (
          <>
            <DataTable>
              <TableHead>
                <Th>Client Name</Th>
                <Th>Client ID</Th>
                <Th>Phone</Th>
                <Th>DOB</Th>
                <Th>Application Date</Th>
                <Th>Queue Age</Th>
                <Th>Status</Th>
              </TableHead>
              <TableBody>
                {items.map((item: any) => (
                  <TableRow key={item.clientId} onClick={() => openDrawer(item.clientId)}>
                    <Td className="font-medium text-blue-600 hover:underline cursor-pointer">
                      {item.fullName}
                    </Td>
                    <Td className="font-mono text-slate-500">{item.clientId}</Td>
                    <Td>{item.phoneNumber}</Td>
                    <Td>{item.dateOfBirth ?? "-"}</Td>
                    <Td>{item.createdAt ? new Date(item.createdAt).toLocaleDateString() : "-"}</Td>
                    <Td>
                      <span className={item.queueAgeDays > 3 ? "text-red-600 font-bold" : ""}>
                        {item.queueAgeDays}d
                      </span>
                    </Td>
                    <Td>
                      <StatusPill tone={kycTone(item.kycStatus)}>{item.kycStatus}</StatusPill>
                    </Td>
                  </TableRow>
                ))}
              </TableBody>
            </DataTable>

            {meta && meta.totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100">
                <p className="text-xs text-slate-500">
                  Page {meta.page} of {meta.totalPages} ({meta.total} total)
                </p>
                <div className="flex gap-2">
                  <Button variant="secondary" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                    Previous
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={page >= meta.totalPages}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </section>

      <Drawer title="KYC Review" open={!!selectedClientId} onClose={closeDrawer}>
        {detailQuery.isLoading && <p className="text-sm text-slate-500">Loading client details...</p>}
        {detail && (
          <div className="space-y-6">
            <section>
              <div className="flex items-center justify-between gap-3 mb-3">
                <h3 className="text-sm font-bold text-slate-700">Client Information</h3>
                <StatusPill tone={kycTone(detail.kycStatus)}>{detail.kycStatus}</StatusPill>
              </div>
              <div className="grid grid-cols-1 gap-3 text-xs sm:grid-cols-2">
                <div>
                  <span className="text-slate-500">Full Name</span>
                  <p className="font-medium text-slate-800">{detail.fullName}</p>
                </div>
                <div>
                  <span className="text-slate-500">Client ID</span>
                  <p className="font-mono text-slate-800">{detail.clientId}</p>
                </div>
                <div>
                  <span className="text-slate-500">Phone</span>
                  <p className="font-medium text-slate-800">{detail.phoneNumber}</p>
                </div>
                <div>
                  <span className="text-slate-500">Alt Phone</span>
                  <p className="font-medium text-slate-800">{detail.altPhone ?? "-"}</p>
                </div>
                <div>
                  <span className="text-slate-500">Date of Birth</span>
                  <p className="font-medium text-slate-800">{detail.dateOfBirth ?? "-"}</p>
                </div>
                <div>
                  <span className="text-slate-500">Gender</span>
                  <p className="font-medium text-slate-800">{detail.gender ?? "-"}</p>
                </div>
                <div className="col-span-2">
                  <span className="text-slate-500">Physical Address</span>
                  <p className="font-medium text-slate-800">{detail.physicalAddress ?? "-"}</p>
                </div>
                <div className="col-span-2">
                  <span className="text-slate-500">National ID</span>
                  <p className="font-mono text-slate-800">{detail.nationalId ?? "-"}</p>
                </div>
              </div>
            </section>

            {detail.activeRequest ? (
              <section className="rounded-lg border border-blue-200 bg-blue-50 p-4">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <h3 className="text-sm font-bold text-slate-800">Active follow-up request</h3>
                    <p className="text-xs text-slate-500">
                      Requested by {detail.activeRequest.requestedByName ?? "Reviewer"} on{" "}
                      {formatDateTime(detail.activeRequest.createdAt)}
                    </p>
                  </div>
                  <StatusPill tone="info">ACTION REQUIRED</StatusPill>
                </div>
                <p className="text-sm text-slate-700 mt-3">{detail.activeRequest.message}</p>
                <div className="mt-4 space-y-2">
                  {detail.activeRequest.items.map((item: any) => (
                    <div key={item.requestItemId} className="rounded-md border border-blue-100 bg-white px-3 py-2">
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <p className="text-xs font-semibold text-slate-800">{item.label}</p>
                          <p className="text-xs text-slate-500">{item.instructions}</p>
                        </div>
                        <StatusPill tone={item.responseStatus === "RESPONDED" ? "success" : "warning"}>
                          {item.responseStatus}
                        </StatusPill>
                      </div>
                    </div>
                  ))}
                </div>
                {isWaitingOnClient ? (
                  <p className="text-xs text-blue-700 mt-4">
                    Waiting for the borrower to update the requested items and resubmit KYC.
                  </p>
                ) : null}
              </section>
            ) : null}

            {detail.requestHistory?.length > 0 ? (
              <section>
                <h3 className="text-sm font-bold text-slate-700 mb-3">Request History</h3>
                <div className="space-y-3">
                  {detail.requestHistory.map((request: any) => (
                    <div key={request.requestId} className="rounded-lg border border-slate-200 p-3">
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <p className="text-xs font-semibold text-slate-800">
                            {request.requestedByName ?? "Reviewer"}
                          </p>
                          <p className="text-[11px] text-slate-500">
                            Requested {formatDateTime(request.createdAt)} · Responded{" "}
                            {formatDateTime(request.respondedAt)}
                          </p>
                        </div>
                        <StatusPill tone="success">{request.requestStatus}</StatusPill>
                      </div>
                      <p className="text-xs text-slate-600 mt-2">{request.message}</p>
                      <div className="mt-3 space-y-1.5">
                        {request.items.map((item: any) => (
                          <div key={item.requestItemId} className="flex items-center justify-between gap-3 rounded border border-slate-100 px-3 py-2">
                            <div>
                              <p className="text-xs font-medium text-slate-700">{item.label}</p>
                              <p className="text-[11px] text-slate-500">{item.instructions}</p>
                            </div>
                            <StatusPill tone={item.responseStatus === "RESPONDED" ? "success" : "warning"}>
                              {item.responseStatus}
                            </StatusPill>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            ) : null}

            <section>
              <h3 className="text-sm font-bold text-slate-700 mb-3">
                KYC Documents ({detail.documents?.length ?? 0})
              </h3>
              {detail.documentGroups?.length === 0 ? (
                <p className="text-xs text-slate-500">No documents uploaded.</p>
              ) : (
                <div className="space-y-4">
                  {detail.documentGroups?.map((group: any) => (
                    <div key={group.documentType} className="rounded-lg border border-slate-200 p-3">
                      <div className="flex items-center justify-between gap-3 mb-3">
                        <div>
                          <p className="text-xs font-bold text-slate-700">
                            {group.documentType.replace(/_/g, " ")}
                          </p>
                          <p className="text-[11px] text-slate-500">
                            {group.totalUploads} upload{group.totalUploads === 1 ? "" : "s"}
                          </p>
                        </div>
                        {group.latestDocument?.verified ? (
                          <StatusPill tone="success">VERIFIED</StatusPill>
                        ) : (
                          <StatusPill tone="warning">LATEST</StatusPill>
                        )}
                      </div>
                      {group.latestDocument ? (
                        <div className="space-y-2">
                          {renderDocumentPreview(detail.clientId, group.latestDocument, (document) =>
                            openDocumentViewer(detail.clientId, group.documentType, document),
                          )}
                          <p className="text-[10px] text-slate-400">
                            {group.latestDocument.fileName} ·{" "}
                            {new Date(group.latestDocument.createdAt).toLocaleString()}
                          </p>
                        </div>
                      ) : null}
                      {group.history?.length > 0 ? (
                        <details className="mt-3 rounded border border-slate-100 bg-slate-50 p-3">
                          <summary className="cursor-pointer text-xs font-semibold text-slate-700">
                            Older uploads ({group.history.length})
                          </summary>
                          <div className="mt-3 space-y-2">
                            {group.history.map((document: any) => (
                              <div key={document.documentId} className="flex items-center justify-between gap-3 rounded border border-slate-200 bg-white px-3 py-2">
                                <div>
                                  <p className="text-xs font-medium text-slate-700">{document.fileName}</p>
                                  <p className="text-[11px] text-slate-500">
                                    {new Date(document.createdAt).toLocaleString()}
                                  </p>
                                </div>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() =>
                                    openDocumentViewer(detail.clientId, group.documentType, document)
                                  }
                                >
                                  Open upload
                                </Button>
                              </div>
                            ))}
                          </div>
                        </details>
                      ) : null}
                    </div>
                  ))}
                </div>
              )}
            </section>

            <section>
              <h3 className="text-sm font-bold text-slate-700 mb-3">
                Next of Kin ({detail.nextOfKin?.length ?? 0})
              </h3>
              {detail.nextOfKin?.length === 0 ? (
                <p className="text-xs text-slate-500">No next of kin records.</p>
              ) : (
                <DataTable>
                  <TableHead>
                    <Th>Name</Th>
                    <Th>Relationship</Th>
                    <Th>Phone</Th>
                  </TableHead>
                  <TableBody>
                    {detail.nextOfKin?.map((nok: any) => (
                      <TableRow key={nok.id}>
                        <Td>{nok.fullName}</Td>
                        <Td>{nok.relationship}</Td>
                        <Td>{nok.phoneNumber}</Td>
                      </TableRow>
                    ))}
                  </TableBody>
                </DataTable>
              )}
            </section>

            {canTakeReviewAction ? (
              <>
                <section>
                  <h3 className="text-sm font-bold text-slate-700 mb-3">Verification Checklist</h3>
                  <div className="space-y-2">
                    {checklist.map((item, idx) => (
                      <div key={item.item} className="flex items-start gap-2 p-2 border border-slate-100 rounded">
                        <input
                          type="checkbox"
                          checked={item.passed}
                          onChange={(e) => {
                            const updated = [...checklist]
                            updated[idx] = { ...updated[idx], passed: e.target.checked }
                            setChecklist(updated)
                          }}
                          className="mt-0.5"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium text-slate-700">{item.item}</p>
                          <input
                            type="text"
                            placeholder="Notes (optional)"
                            value={item.note}
                            onChange={(e) => {
                              const updated = [...checklist]
                              updated[idx] = { ...updated[idx], note: e.target.value }
                              setChecklist(updated)
                            }}
                            className="mt-1 w-full text-xs px-2 py-1 border border-slate-200 rounded"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </section>

                <section className="border-t border-slate-200 pt-4 space-y-5">
                  <div>
                    <h3 className="text-sm font-bold text-slate-700 mb-3">Request More Information</h3>
                    <FormField label="Summary for borrower" className="mb-4">
                      <Textarea
                        rows={3}
                        placeholder="Explain exactly what the borrower should correct or re-upload."
                        value={requestMessage}
                        onChange={(e) => setRequestMessage(e.target.value)}
                      />
                    </FormField>
                    <div className="space-y-2">
                      {requestItems.map((item) => (
                        <div key={`${item.itemKind}:${item.itemKey}`} className="rounded-md border border-slate-200 p-3">
                          <label className="flex items-start gap-2">
                            <input
                              type="checkbox"
                              checked={item.selected}
                              onChange={(event) =>
                                updateRequestItem(item.itemKind, item.itemKey, (current) => ({
                                  ...current,
                                  selected: event.target.checked,
                                  instructions: event.target.checked ? current.instructions : "",
                                }))
                              }
                              className="mt-1"
                            />
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-semibold text-slate-800">{item.label}</p>
                              <p className="text-[11px] text-slate-500">{item.itemKind.replace(/_/g, " ")}</p>
                              {item.selected ? (
                                <textarea
                                  rows={2}
                                  value={item.instructions}
                                  onChange={(event) =>
                                    updateRequestItem(item.itemKind, item.itemKey, (current) => ({
                                      ...current,
                                      instructions: event.target.value,
                                    }))
                                  }
                                  placeholder="Tell the borrower what to fix or upload."
                                  className="mt-2 w-full rounded-md border border-slate-200 px-3 py-2 text-xs focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                                />
                              ) : null}
                            </div>
                          </label>
                        </div>
                      ))}
                    </div>
                    <div className="mt-4 flex justify-end">
                      <Button
                        variant="secondary"
                        onClick={() => {
                          if (!validateRequestDraft()) return
                          setConfirmModal("REQUEST_MORE_INFO")
                        }}
                        disabled={requestMutation.isPending}
                      >
                        Request More Info
                      </Button>
                    </div>
                    {requestMutation.isError ? (
                      <p className="text-xs text-red-600 mt-2">
                        {(requestMutation.error as any)?.message ?? "Failed to create the KYC request."}
                      </p>
                    ) : null}
                  </div>

                  <div className="border-t border-slate-200 pt-4">
                    <h3 className="text-sm font-bold text-slate-700 mb-3">Final Decision</h3>
                    <FormField label="Rejection Reason (required if rejecting)" className="mb-4">
                      <Textarea
                        rows={3}
                        placeholder="Explain why the KYC application is being rejected..."
                        value={rejectionReason}
                        onChange={(e) => setRejectionReason(e.target.value)}
                      />
                    </FormField>
                    <div className="flex gap-3">
                      <Button
                        variant="primary"
                        onClick={() => setConfirmModal("APPROVED")}
                        disabled={reviewMutation.isPending}
                      >
                        Approve KYC
                      </Button>
                      <Button
                        variant="danger-solid"
                        onClick={() => {
                          if (!rejectionReason.trim()) {
                            alert("Please provide a rejection reason.")
                            return
                          }
                          setConfirmModal("REJECTED")
                        }}
                        disabled={reviewMutation.isPending}
                      >
                        Reject KYC
                      </Button>
                    </div>
                    {reviewMutation.isError ? (
                      <p className="text-xs text-red-600 mt-2">
                        {(reviewMutation.error as any)?.message ?? "Failed to submit review."}
                      </p>
                    ) : null}
                  </div>
                </section>
              </>
            ) : null}
          </div>
        )}
      </Drawer>

      <Modal
        open={!!confirmModal}
        onClose={() => setConfirmModal(null)}
        title={
          confirmModal === "APPROVED"
            ? "Confirm Approval"
            : confirmModal === "REJECTED"
              ? "Confirm Rejection"
              : "Confirm Follow-Up Request"
        }
        description={
          confirmModal === "APPROVED"
            ? "Are you sure you want to approve this client's KYC? This will allow loan processing to proceed."
            : confirmModal === "REJECTED"
              ? "Are you sure you want to reject this client's KYC? This is a final KYC outcome."
              : `The borrower will see ${selectedRequestItems.length} requested item${
                  selectedRequestItems.length === 1 ? "" : "s"
                } and must respond before the case returns to review.`
        }
      >
        <div className="flex justify-end gap-3">
          <Button variant="secondary" onClick={() => setConfirmModal(null)}>
            Cancel
          </Button>
          <Button
            variant={confirmModal === "REJECTED" ? "danger-solid" : "primary"}
            onClick={handleConfirm}
            disabled={reviewMutation.isPending || requestMutation.isPending}
          >
            {reviewMutation.isPending || requestMutation.isPending
              ? "Submitting..."
              : confirmModal === "APPROVED"
                ? "Confirm Approval"
                : confirmModal === "REJECTED"
                  ? "Confirm Rejection"
                  : "Send Request"}
          </Button>
        </div>
      </Modal>

      <Modal
        open={Boolean(documentViewer)}
        onClose={() => setDocumentViewer(null)}
        title={documentViewer?.document.fileName ?? "Document viewer"}
        description={
          documentViewer
            ? `${documentViewer.documentType.replace(/_/g, " ")} · Uploaded ${formatDateTime(
                documentViewer.document.createdAt,
              )}`
            : undefined
        }
        widthClassName="max-w-5xl"
      >
        {documentViewer ? (
          <div className="space-y-4">
            <div className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-xs text-slate-600">
              <div>
                <p className="font-semibold text-slate-800">{documentViewer.document.fileName}</p>
                <p className="mt-1">
                  {documentViewer.document.mimeType} ·{" "}
                  {documentViewer.document.verified ? "Verified" : "Unverified"}
                </p>
              </div>
              <a
                href={getKycDocumentUrl(documentViewer.clientId, documentViewer.document.documentId)}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm font-semibold text-blue-600 hover:underline"
              >
                Open in new tab
              </a>
            </div>

            {isImageDocument(documentViewer.document) ? (
              <div className="overflow-hidden rounded-xl border border-slate-200 bg-slate-950/95 p-4">
                <img
                  src={getKycDocumentUrl(documentViewer.clientId, documentViewer.document.documentId)}
                  alt={documentViewer.document.fileName}
                  className="mx-auto max-h-[72vh] w-auto max-w-full object-contain"
                />
              </div>
            ) : (
              <div className="flex min-h-[24rem] flex-col items-center justify-center rounded-xl border border-dashed border-slate-300 bg-slate-50 px-6 py-10 text-center">
                <p className="text-sm font-semibold text-slate-800">Preview is not available inline for this upload.</p>
                <p className="mt-2 max-w-md text-sm text-slate-500">
                  Open the file in a new tab to inspect it individually. This avoids browser frame restrictions on protected document responses.
                </p>
                <a
                  href={getKycDocumentUrl(documentViewer.clientId, documentViewer.document.documentId)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-5 inline-flex items-center justify-center rounded-md border border-blue-600 bg-white px-4 py-2 text-sm font-bold text-blue-600 transition-colors hover:bg-blue-50"
                >
                  Open Document
                </a>
              </div>
            )}
          </div>
        ) : null}
      </Modal>
    </div>
  )
}
